import { MaterialPricing, TimberItem, PricingCache } from '../types/bidding';

// Google Sheets public URL - will be converted to CSV export format
const DEFAULT_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRmUh2dyDKmiPJdKri7Rd8R7cgDfIPjYjT_cHhin9iLqfGvWYfcN2xkNhEQzVWxafMw11B0ughmMj7c/pub?output=csv';

/**
 * Convert a Google Sheets URL to CSV export format
 */
function convertToCsvUrl(url: string): string {
  // If already a CSV URL, return as-is
  if (url.includes('output=csv')) {
    return url;
  }
  
  // Convert pubhtml to pub with CSV output
  if (url.includes('/pubhtml')) {
    return url.replace('/pubhtml', '/pub?output=csv');
  }
  
  // If it's a regular sheets URL, try to convert it
  const sheetIdMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
  if (sheetIdMatch) {
    const sheetId = sheetIdMatch[1];
    return `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
  }
  
  return url;
}

/**
 * Fetch material pricing data from public Google Sheet
 * @param sheetUrl - The public URL of the Google Sheet (will be converted to CSV)
 * @returns Array of MaterialPricing objects
 */
export async function fetchMaterialPricing(
  sheetUrl: string = DEFAULT_SHEET_URL
): Promise<MaterialPricing[]> {
  try {
    // Convert to CSV format for reliable parsing
    const csvUrl = convertToCsvUrl(sheetUrl);
    console.log('[GoogleSheets] Fetching from:', csvUrl);
    
    const response = await fetch(csvUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch sheet: ${response.status} ${response.statusText}`);
    }
    
    const csvText = await response.text();
    console.log('[GoogleSheets] Received CSV data, length:', csvText.length);
    
    // Parse CSV
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('Sheet must have headers and at least one data row');
    }
    
    // Parse CSV headers
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
    console.log('[GoogleSheets] Found headers:', headers);
    
    // Find column indices with more flexible matching
    const nameIndex = headers.findIndex(h => 
      h.includes('name') || 
      h.includes('item') || 
      h.includes('description') ||
      h.includes('prod')
    );
    const materialIndex = headers.findIndex(h => 
      h.includes('material') || 
      h.includes('species') ||
      h.includes('type') ||
      h.includes('grade')
    );
    const widthIndex = headers.findIndex(h => h.includes('width') || h === 'w');
    const heightIndex = headers.findIndex(h => h.includes('height') || h === 'h' || h.includes('thick'));
    const orderLengthIndex = headers.findIndex(h => 
      (h.includes('order') && h.includes('length')) ||
      h.includes('length') ||
      h.includes('len') ||
      h === 'l'
    );
    const priceIndex = headers.findIndex(h => 
      h.includes('price') || 
      h.includes('cost') ||
      h.includes('$') ||
      h.includes('unit')
    );
    
    console.log('[GoogleSheets] Column mapping:', {
      nameIndex,
      materialIndex,
      widthIndex,
      heightIndex,
      orderLengthIndex,
      priceIndex
    });
    
    if (priceIndex === -1) {
      throw new Error(`Required price column not found. Available columns: ${headers.join(', ')}`);
    }
    
    if (nameIndex === -1 && materialIndex === -1) {
      throw new Error(`No identifier column found (need name, item, or material). Available columns: ${headers.join(', ')}`);
    }
    
    // Parse data rows
    const pricingData: MaterialPricing[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const cells = parseCSVLine(lines[i]);
      
      if (cells.length === 0) continue;
      
      const name = nameIndex >= 0 ? cells[nameIndex]?.trim() || '' : '';
      const material = materialIndex >= 0 ? cells[materialIndex]?.trim() || '' : '';
      const widthStr = widthIndex >= 0 ? cells[widthIndex]?.trim() || '0' : '0';
      const heightStr = heightIndex >= 0 ? cells[heightIndex]?.trim() || '0' : '0';
      const orderLength = orderLengthIndex >= 0 ? cells[orderLengthIndex]?.trim() || '' : '';
      const priceStr = priceIndex >= 0 ? cells[priceIndex]?.trim() || '0' : '0';
      
      if (!name && !material) continue; // Skip empty rows
      
      // Parse numbers from strings (remove $ and commas)
      const width = parseFloat(widthStr.replace(/[^0-9.]/g, ''));
      const height = parseFloat(heightStr.replace(/[^0-9.]/g, ''));
      const unitPrice = parseFloat(priceStr.replace(/[^0-9.]/g, ''));
      
      pricingData.push({
        name: name.toLowerCase(),
        material: material,
        width: isNaN(width) ? 0 : width,
        height: isNaN(height) ? 0 : height,
        orderLength: orderLength,
        unitPrice: isNaN(unitPrice) ? 0 : unitPrice,
      });
    }
    
    console.log(`[GoogleSheets] Fetched ${pricingData.length} pricing records`);
    if (pricingData.length > 0) {
      console.log('[GoogleSheets] Sample pricing record:', pricingData[0]);
    }
    
    return pricingData;
  } catch (error) {
    console.error('Error fetching material pricing from Google Sheets:', error);
    throw error;
  }
}

/**
 * Match a timber item to pricing data and return the unit price
 * @param item - The timber item to price
 * @param pricingData - Array of pricing data from Google Sheets
 * @returns The unit price for the item, or 0 if not found
 */
export function matchTimberPrice(
  item: TimberItem,
  pricingData: MaterialPricing[]
): number {
  // Normalize item data for matching
  const itemName = item.name.toLowerCase().trim();
  const itemMaterial = item.material.toLowerCase().trim();
  const itemWidth = item.width;
  const itemHeight = item.height;
  const itemOrderLength = normalizeLength(item.orderLength);
  
  console.log(`[Pricing] Matching item:`, {
    name: itemName,
    material: itemMaterial,
    width: itemWidth,
    height: itemHeight,
    orderLength: itemOrderLength,
  });
  
  // Try exact match first
  let match = pricingData.find(p => {
    const nameMatch = p.name.toLowerCase() === itemName;
    const materialMatch = !p.material || p.material.toLowerCase().includes(itemMaterial) || itemMaterial.includes(p.material.toLowerCase());
    const widthMatch = !p.width || p.width === itemWidth;
    const heightMatch = !p.height || p.height === itemHeight;
    const lengthMatch = !p.orderLength || normalizeLength(p.orderLength) === itemOrderLength;
    
    return nameMatch && materialMatch && widthMatch && heightMatch && lengthMatch;
  });
  
  if (match) {
    console.log(`[Pricing] ✓ Exact match found: $${match.unitPrice}`);
    return match.unitPrice;
  }
  
  // If no exact match, try fuzzy match (name + dimensions)
  match = pricingData.find(p => {
    const nameMatch = p.name.toLowerCase() === itemName;
    const widthMatch = !p.width || p.width === itemWidth;
    const heightMatch = !p.height || p.height === itemHeight;
    
    return nameMatch && widthMatch && heightMatch;
  });
  
  if (match) {
    console.log(`[Pricing] ✓ Fuzzy match (name + dims) found: $${match.unitPrice}`);
    return match.unitPrice;
  }
  
  // If still no match, try partial name match
  match = pricingData.find(p => {
    return itemName.includes(p.name.toLowerCase()) || p.name.toLowerCase().includes(itemName);
  });
  
  if (match) {
    console.log(`[Pricing] ✓ Partial match found: $${match.unitPrice}`);
    return match.unitPrice;
  }
  
  console.warn(`[Pricing] ✗ No match found for "${itemName}" (${itemWidth}x${itemHeight})`);
  return 0;
}

/**
 * Parse a CSV line, handling quoted fields properly
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add last field
  result.push(current);
  
  return result;
}

/**
 * Normalize length string for comparison
 * Converts various formats like "8'", "8 ft", "8 feet" to "8'"
 */
function normalizeLength(length: string): string {
  if (!length) return '';
  
  // Remove spaces and convert to lowercase
  let normalized = length.toLowerCase().trim();
  
  // Convert feet/ft to '
  normalized = normalized.replace(/\s*feet\s*/g, "'");
  normalized = normalized.replace(/\s*ft\s*/g, "'");
  
  // Remove inches notation if it's just 0"
  normalized = normalized.replace(/\s*0\s*"/g, '');
  
  // Standardize format
  normalized = normalized.replace(/\s+/g, '');
  
  return normalized;
}

/**
 * Generate a cache key for pricing lookups
 */
export function generatePricingCacheKey(item: TimberItem): string {
  return `${item.name}-${item.material}-${item.width}-${item.height}-${normalizeLength(item.orderLength)}`;
}

/**
 * Update pricing cache with new data
 */
export function updatePricingCache(
  cache: PricingCache,
  pricingData: MaterialPricing[]
): PricingCache {
  const now = new Date();
  const updated: PricingCache = { ...cache };
  
  pricingData.forEach(pricing => {
    const key = `${pricing.name}-${pricing.material}-${pricing.width}-${pricing.height}-${normalizeLength(pricing.orderLength)}`;
    updated[key] = {
      unitPrice: pricing.unitPrice,
      lastUpdated: now,
    };
  });
  
  return updated;
}

/**
 * Get pricing from cache
 */
export function getPricingFromCache(
  cache: PricingCache,
  item: TimberItem
): number | null {
  const key = generatePricingCacheKey(item);
  const cached = cache[key];
  
  if (!cached) return null;
  
  // Check if cache is stale (older than 24 hours)
  const ageMs = Date.now() - cached.lastUpdated.getTime();
  const maxAgeMs = 24 * 60 * 60 * 1000; // 24 hours
  
  if (ageMs > maxAgeMs) {
    return null; // Cache expired
  }
  
  return cached.unitPrice;
}

/**
 * Batch price multiple timber items
 * Tries Firestore pricing first, then falls back to Google Sheets
 */
export async function priceTimberItems(
  items: TimberItem[],
  cache?: PricingCache
): Promise<TimberItem[]> {
  // Try to use cache first
  if (cache) {
    const pricedItems = items.map(item => {
      const cachedPrice = getPricingFromCache(cache, item);
      if (cachedPrice !== null) {
        return {
          ...item,
          unitPrice: cachedPrice,
          totalPrice: item.quantity * cachedPrice,
        };
      }
      return item;
    });
    
    // If all items were priced from cache, return early
    if (pricedItems.every(item => item.unitPrice > 0)) {
      return pricedItems;
    }
  }
  
  // Try Firestore pricing first
  try {
    console.log('[Pricing] Trying Firestore pricing data...');
    const { priceTimberItemsFromFirestore } = await import('./timberPricingService');
    const pricedItems = await priceTimberItemsFromFirestore(items);
    
    const pricedCount = pricedItems.filter(item => item.unitPrice > 0).length;
    console.log(`[Pricing] Firestore priced ${pricedCount}/${items.length} items`);
    
    // If we got some prices from Firestore, use them
    if (pricedCount > 0) {
      return pricedItems;
    }
  } catch (error) {
    console.warn('[Pricing] Firestore pricing failed, falling back to Google Sheets:', error);
  }
  
  // Fallback to Google Sheets
  console.log('[Pricing] Fetching fresh pricing data from Google Sheets...');
  const pricingData = await fetchMaterialPricing();
  
  // Price all items
  console.log(`[Pricing] Pricing ${items.length} items with Google Sheets...`);
  const pricedItems = items.map(item => {
    const unitPrice = matchTimberPrice(item, pricingData);
    return {
      ...item,
      unitPrice,
      totalPrice: item.quantity * unitPrice,
    };
  });
  
  const pricedCount = pricedItems.filter(item => item.unitPrice > 0).length;
  console.log(`[Pricing] Successfully priced ${pricedCount}/${items.length} items`);
  
  return pricedItems;
}
